/**
 *  Return the index of the first element that is equal to the reference value.
 *  If there are no values equal values it will return -1.
 *
 *  Note: Assumes entries is already sorted by compareFn
 */
export function binarySearchExact<T>(
  entries: T[],
  reference: T,
  compareFn: (a: T, b: T) => number
): number {
  const index = binarySearch(entries, reference, compareFn);
  if ((index != -1) && (compareFn(entries[index], reference) == 0)) {
    return index;
  }
  return -1;
}

/**
 *  Return the index of the first element that is greater OR equal to the
 *  reference value. If there are no values greater or equal values it
 *  will return -1.
 *
 *  Note: Assumes entries is already sorted by compareFn
 */
export function binarySearch<T>(
  entries: T[],
  reference: T,
  compareFn: (a: T, b: T) => number
): number {
  if (entries.length === 0) {
    return -1;
  }
  const indexMinusOne =
    binarySearchRecursive(entries, reference, compareFn, 0, entries.length);

  // Check for corner case where the reference value is the first in the
  // entries array
  if (compareFn(entries[indexMinusOne], reference) < 0) {
    const index = indexMinusOne + 1;
    if (entries.length <= index) {
      return -1;
    }
    return index;
  } else {
    return indexMinusOne;
  }
}

/// ---

/**
 *  This function will use binary search to find the highest ingest that is
 *  LESS than the reference value. In order to find the first occurrence of
 *  reference 1 must be added to the index returned by this function, unless
 *  the first element in entries is equal to reference in which case it will
 *  return the index of the first matching value. This needs to be checked
 *  using the compareFn outside of this function.
 */
function binarySearchRecursive<T>(
  entries: T[],
  reference: T,
  compareFn: (a: T, b: T) => number,
  start: number,
  end: number
): number {
  if ((end - start) == 1) {
    return start;
  }
  const midPoint = Math.floor((start + end) / 2.0);
  const entry = entries[midPoint];
  if (compareFn(reference, entry) <= 0) {
    return binarySearchRecursive(entries, reference, compareFn, start, midPoint);
  } else {
    return binarySearchRecursive(entries, reference, compareFn, midPoint, end);
  }
}

